import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Input, Button, Tooltip, Badge } from 'antd';
import { SearchOutlined, SendOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styles from './styles/muliple-select-input.module.css';
import sharedStyles from './styles/shared-components.module.css';
import { addAnswer } from '@/store/slices/chatbotFlow';
import {
  addSelectedOptions,
  removeSelectedOptions,
  resetSelectedOptions,
} from '@/store/slices/chatbotInput';

const SelectInput = ({
                       block,
                       selectedOptions,
                       addSelectedOptions,
                       removeSelectedOptions,
                       addAnswer,
                       resetSelectedOptions,
                       isChatbotVisible,
                       animTransition,
                     }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onSearch = (e) => {
    const searchText = e.target.value;
    setShowError(false);
    setErrorMessage('');
    setInput(searchText);
    const debouncedSearch = debounce(() => {
      const filteredOptions = searchText?.length === 0
        ? []
        : block?.options?.selectOptions?.filter(option => option.title?.toLowerCase().includes(searchText?.toLowerCase()));
      setOptions(filteredOptions);
    }, 250);
    debouncedSearch();
  };

  const debounce = (func, timeout) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  const submitInput = () => {
    if (selectedOptions.length === 0) {
      setShowError(true);
      setErrorMessage('Please select at least one option before submitting.');
      return;
    }

    setShowError(false);
    setErrorMessage('');

    addAnswer(selectedOptions);
    resetSelectedOptions();
  };

  const renderOptions = () => {
    return options?.map((option, i) => {
      const isChecked = selectedOptions?.some(selected => selected.id === option.id);
      return (
        <div
          key={i}
          className={`${isChecked ? styles.checked : styles.not_checked} ${styles.option}`}
          onClick={() => {
            isChecked
              ? removeSelectedOptions(option.id)
              : addSelectedOptions({ ...option, parent_id: null });
          }}
        >
          {option.title}
        </div>
      );
    });
  };

  return (
    <div className={sharedStyles.wrapper}>
      {options.length !== 0 && <div className={styles.options_container}>
        {renderOptions()}
      </div>}
      <div className={styles.input_container}>
        <Input
          className={styles.input}
          value={input}
          autoFocus
          prefix={<SearchOutlined className={styles.icon_prefix} />}
          placeholder="Search..."
          onChange={onSearch}
          variant={'borderless'}
          onPressEnter={submitInput}
        />
        <Tooltip
          placement="topRight"
          title={errorMessage}
          align={{ offset: [-5, -5] }}
          open={showError && isChatbotVisible && animTransition}
        >
          <Badge offset={[-20,11]} size={"small"}
            count={selectedOptions.length}
            style={{ backgroundColor: '#969696' }} // Grayish color for the badge
          >
            <Button
              className={sharedStyles.send_button}
              type="text"
              danger={showError}
              onClick={submitInput}
              icon={<SendOutlined style={{ fontSize: '24px' }} />}
            />
          </Badge>
        </Tooltip>
      </div>
    </div>
  );
};

SelectInput.propTypes = {
  block: PropTypes.object.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  addSelectedOptions: PropTypes.func.isRequired,
  removeSelectedOptions: PropTypes.func.isRequired,
  isChatbotVisible: PropTypes.bool.isRequired,
  animTransition: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  selectedOptions: state.chatbotInput.selectedOptions,
  isChatbotVisible: state.chatbotVisibility.isVisible,
  animTransition: state.chatbotVisibility.animTransition,
});

const mapDispatchToProps = {
  addSelectedOptions,
  removeSelectedOptions,
  addAnswer,
  resetSelectedOptions,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectInput);
